import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Store, Pencil, Bot, ExternalLink } from "lucide-react";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/vendors"); setVendors(data || []); }
    catch { toast.error("Failed to load vendors"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editor.name.trim()) { toast.error("Vendor name required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/vendors", { name: editor.name.trim(), logo_url: editor.logo_url, bio: editor.bio, website: editor.website });
      toast.success("Vendor profile saved");
      setEditor(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-vendors">
      <div className="flex items-center gap-2">
        <Store className="h-5 w-5 text-brandblue" />
        <div>
          <h3 className="font-display text-xl font-bold">Bot Vendors</h3>
          <p className="text-xs text-fx-s3">Manage vendor profiles (logo, bio, website). Vendors are derived from the vendor names set on bots — add a profile to enrich their marketplace page.</p>
        </div>
      </div>

      {vendors.length === 0 ? (
        <p className="text-sm text-fx-s3">No vendors yet. Set a Vendor name on a bot (Bots tab), then add its profile here.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {vendors.map((v) => (
            <div key={v.slug} data-testid={`vendor-row-${v.slug}`} className="rounded-xl bg-fx-s1 border border-white/5 p-4 flex items-start gap-3">
              {v.logo_url ? <img src={v.logo_url} alt="" className="h-12 w-12 rounded-lg object-cover bg-fx-s2" /> : <div className="h-12 w-12 rounded-lg bg-fx-s2 grid place-items-center text-brandblue"><Bot className="h-6 w-6" /></div>}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{v.name}</div>
                <div className="text-xs text-fx-s3">{v.bot_count} bot{v.bot_count === 1 ? "" : "s"}{v.website ? " · " + v.website.replace(/^https?:\/\//, "") : ""}</div>
                <p className="text-xs text-fx-s3 mt-1 line-clamp-2">{v.bio || <span className="italic opacity-70">No bio yet</span>}</p>
              </div>
              <Button size="sm" variant="outline" data-testid={`vendor-edit-${v.slug}`} onClick={() => setEditor({ ...v })} className="border-white/10 bg-fx-base h-8 gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
        <DialogContent className="bg-fx-s1 border-white/10">
          <DialogHeader><DialogTitle>Vendor Profile</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Vendor name</Label><Input data-testid="vendor-name-input" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Logo URL</Label><Input data-testid="vendor-logo-input" value={editor.logo_url || ""} onChange={(e) => setEditor({ ...editor, logo_url: e.target.value })} placeholder="https://…" className="bg-fx-base border-white/10 font-mono text-xs" /></div>
              <div className="space-y-1"><Label className="text-xs">Website</Label><Input data-testid="vendor-website-input" value={editor.website || ""} onChange={(e) => setEditor({ ...editor, website: e.target.value })} placeholder="https://vendor.com" className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Bio / description</Label><textarea data-testid="vendor-bio-input" value={editor.bio || ""} onChange={(e) => setEditor({ ...editor, bio: e.target.value })} rows={4} placeholder="Tell users about this vendor…" className="w-full rounded-md bg-fx-base border border-white/10 text-sm p-2 text-white" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} className="border-white/10">Cancel</Button>
            <Button data-testid="vendor-save-btn" disabled={busy} onClick={save} className="bg-brandblue hover:bg-blue-500">Save profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
